import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './style.scss';
import { forwardRef, useEffect, useRef, useState } from 'react';
import RemoveIcon from '../../assets/icons/close.svg';
import ChevronIcon from '../../assets/icons/chevron.svg';
import { useTranslation } from 'react-i18next';
import { useCloseSelect } from '../../hooks/closeSelect';
const MultipleSelect = forwardRef(({ label, placeholder, options, defaultSelectedOptionsValues = [], onChange, manageIsSelected = true, highlightSelected = true, disabled, error, errorMessage, }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOptionsValues, setSelectedOptionsValues] = useState(defaultSelectedOptionsValues);
    const [wasChanged, setWasChanged] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [openedByKeyboard, setOpenedByKeyboard] = useState(false);
    const dropdownRef = useRef(null);
    useCloseSelect(dropdownRef, () => {
        setIsOpen(false);
        setFocusedIndex(-1);
    });
    useEffect(() => {
        if (defaultSelectedOptionsValues) {
            setSelectedOptionsValues(defaultSelectedOptionsValues);
        }
    }, [defaultSelectedOptionsValues]);
    useEffect(() => {
        if (defaultSelectedOptionsValues) {
            setSelectedOptionsValues(defaultSelectedOptionsValues);
        }
    }, [defaultSelectedOptionsValues]);
    useEffect(() => {
        if (wasChanged) {
            onChange(selectedOptionsValues);
        }
    }, [selectedOptionsValues]);
    const onOptionChange = (value) => {
        if (disabled) {
            return;
        }
        setSelectedOptionsValues((prev) => {
            const isSelected = prev.includes(value);
            return isSelected ? prev.filter((v) => v !== value) : [...prev, value];
        });
        setWasChanged(true);
    };
    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen((prev) => {
                const nextOpen = !prev;
                if (nextOpen) {
                    setFocusedIndex(0);
                    setOpenedByKeyboard(false);
                }
                else {
                    setFocusedIndex(-1);
                }
                return nextOpen;
            });
        }
    };
    const resetValue = (event) => {
        event.stopPropagation();
        if (!disabled) {
            setSelectedOptionsValues([]);
            onChange([]);
            setIsOpen(false);
        }
    };
    const getOptionLabel = (optionValue) => {
        return options.find((option) => option.value === optionValue)?.label;
    };
    const displayLabel = selectedOptionsValues.length === 0
        ? placeholder || t('placeholder.select_an_option')
        : selectedOptionsValues.length === 1
            ? getOptionLabel(selectedOptionsValues[0])
            : `${getOptionLabel(selectedOptionsValues[0])}, +${selectedOptionsValues.length - 1}`;
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            return;
        }
        if (!isOpen) {
            if (e.code === 'Space') {
                e.preventDefault();
                setIsOpen(true);
                setFocusedIndex(0);
                setOpenedByKeyboard(true);
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex((prev) => (prev + 1) % options.length);
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((prev) => (prev - 1 + options.length) % options.length);
        }
        else if (e.key === ' ') {
            e.preventDefault();
            if (focusedIndex >= 0) {
                onOptionChange(options[focusedIndex].value);
            }
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
            setFocusedIndex(-1);
        }
    };
    return (_jsxs(_Fragment, { children: [label && _jsx("div", { className: "label", children: label }), _jsxs("div", { className: `multiple-select ${isOpen ? ' is-open' : ''}`, ref: dropdownRef, children: [_jsxs("button", { ref: ref, type: "button", "aria-haspopup": "listbox", "aria-expanded": isOpen, "aria-activedescendant": focusedIndex >= 0 ? `option-${options[focusedIndex].value}` : undefined, className: `multiple-select-value` +
                            `${highlightSelected && selectedOptionsValues.length > 0 ? ' selected' : ''}` +
                            `${error ? ' form-field-error' : ''}`, onClick: toggleDropdown, onKeyDown: handleKeyDown, children: [_jsx("span", { className: selectedOptionsValues.length === 0 ? 'placeholder' : undefined, children: displayLabel }), _jsx("span", { tabIndex: manageIsSelected && selectedOptionsValues.length > 0 ? 0 : -1, className: "multiple-select-icon", onClick: manageIsSelected && selectedOptionsValues.length > 0 ? resetValue : undefined, onKeyDown: (e) => {
                                    if (e.code === 'Space') {
                                        e.preventDefault();
                                        resetValue(e);
                                    }
                                }, children: manageIsSelected ? (selectedOptionsValues.length > 0 ? (disabled ? (_jsx(ChevronIcon, {})) : (_jsx(RemoveIcon, {}))) : (_jsx(ChevronIcon, {}))) : (_jsx(ChevronIcon, {})) })] }), _jsx("div", { className: "multiple-select-options", children: _jsx("div", { className: "multiple-select-options-wrapper", role: "listbox", children: options.map((option, index) => {
                                const isSelected = selectedOptionsValues.includes(option.value);
                                const isFocused = index === focusedIndex;
                                return (_jsxs("div", { id: `option-${option.value}`, className: `option${isSelected ? ' option--selected' : ''}${openedByKeyboard && isFocused ? ' option--focused' : ''}`, onClick: () => onOptionChange(option.value), role: "option", "aria-selected": isSelected, children: [_jsx("span", { className: "option-checkbox" }), _jsx("span", { className: "option-label", children: option.label })] }, option.value));
                            }) }) })] }), error && errorMessage && _jsx("div", { className: "form-field-error-message", children: errorMessage })] }));
});
export default MultipleSelect;
